import { AlarmClock, CheckSquare, MonitorPlay, PhoneCall, type LucideIcon } from 'lucide-react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Skeleton } from '@/components/ui/skeleton'
import { useAuth } from '@/lib/auth/AuthProvider'
import { cn } from '@/lib/utils'
import { useActiveDemos, useDemoReport, useMyFollowups, useMySummary } from '../api'
import type { WidgetBodyProps } from '../types'
import { TONE_STYLES } from './tones'

type KpiProps = WidgetBodyProps & { label: string; value: ReactNode; hint?: ReactNode; icon: LucideIcon; loading: boolean; alert?: boolean }

function Kpi({ config, label, value, hint, icon: Icon, loading, alert }: KpiProps) {
  const compact = config.density === 'compact'
  return (
    <div className="flex h-full items-start justify-between gap-3">
      <div className="min-w-0 space-y-1">
        <p className="truncate text-xs font-medium text-muted-foreground">{label}</p>
        {loading ? (
          <Skeleton className="h-8 w-14" />
        ) : (
          <p className={cn('font-semibold tabular-nums tracking-tight', compact ? 'text-2xl' : 'text-3xl', alert && 'text-destructive')}>{value}</p>
        )}
        {hint ? <p className="truncate text-xs text-muted-foreground">{hint}</p> : null}
      </div>
      <span className={cn('hidden shrink-0 items-center justify-center rounded-xl ring-1 ring-inset @min-[10rem]:flex', compact ? 'size-8' : 'size-10', TONE_STYLES[config.tone].icon)}>
        <Icon className={compact ? 'size-4' : 'size-5'} aria-hidden />
      </span>
    </div>
  )
}

export function KpiOpenTasks({ config }: WidgetBodyProps) {
  const { t } = useTranslation()
  const { can } = useAuth()
  const q = useMySummary(can('tasks.write'))
  const open = q.data?.open.reduce((s, c) => s + c.count, 0) ?? 0
  const hint = q.data ? `${t('dashboard.dueToday')}: ${q.data.due_today}` : undefined
  return <Kpi config={config} label={t('dashboard.myTasks')} value={open} hint={hint} icon={CheckSquare} loading={q.isLoading} />
}

export function KpiOverdueTasks({ config }: WidgetBodyProps) {
  const { t } = useTranslation()
  const { can } = useAuth()
  const q = useMySummary(can('tasks.write'))
  const overdue = q.data?.overdue ?? 0
  return <Kpi config={config} label={t('dashboard.overdueTasks')} value={overdue} icon={AlarmClock} loading={q.isLoading} alert={overdue > 0} />
}

export function KpiOverdueFollowups({ config }: WidgetBodyProps) {
  const { t } = useTranslation()
  const { can } = useAuth()
  const q = useMyFollowups(can('activities.write'))
  const overdue = q.data?.filter((f) => f.overdue).length ?? 0
  return <Kpi config={config} label={t('dashboard.overdueFollowups')} value={overdue} icon={PhoneCall} loading={q.isLoading} alert={overdue > 0} />
}

export function KpiActiveDemos({ config }: WidgetBodyProps) {
  const { t } = useTranslation()
  const { can } = useAuth()
  const demos = useActiveDemos(can('accounts.read'))
  const report = useDemoReport(can('reports.read'))
  const rate = report.data?.conversion_rate_percent
  const hint = rate != null ? `${t('dashboard.demoConversion')}: %${rate}` : undefined
  return <Kpi config={config} label={t('dashboard.activeDemos')} value={demos.data?.length ?? 0} hint={hint} icon={MonitorPlay} loading={demos.isLoading} />
}
